import { Dispatch, FC, SetStateAction, Suspense, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { addDays, endOfWeek, format, isBefore, min, startOfWeek, sub } from 'date-fns';
import { PreloadedQuery, UseQueryLoaderLoadQueryOptions } from 'react-relay';
import {
  IndividualStudentReportStatsQuery,
  IndividualStudentReportStatsQuery$variables,
} from '../../graphQLOperations/__generated__/IndividualStudentReportStatsQuery.graphql';
import { IndividualReportWeeklyStats } from './IndividualReportWeeklyStats';
import { splitId } from '../../businessLogicService/splitIdsFromBackEnd';
import { IndividualReportDailyActivity } from './IndividualReportDailyActivity';
import { ThreeDotLoader } from '../Fallback/ThreeDotsLoader';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  selectedIndividualStudentReportState,
  setIndividualStudentReport,
} from '../../redux/reducers/individualStudentReportSlice';
import { SelectedSkillIndividualReport } from './IndividualReportWrapper';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';

interface Props {
  studentId: string;
  weeklyStatsRef: PreloadedQuery<IndividualStudentReportStatsQuery, Record<string, unknown>> | undefined | null;
  loadWeeklyStats: (
    variables: IndividualStudentReportStatsQuery$variables,
    options?: UseQueryLoaderLoadQueryOptions,
  ) => void;
  setSelectedSkill: Dispatch<SetStateAction<SelectedSkillIndividualReport | null>>;
  setSelectedModalSkill: Dispatch<SetStateAction<{ code: string } | null>>;
  siteId?: string | null;
}

export const IndividualReportSkillLearningActivity: FC<Props> = ({
  weeklyStatsRef,
  loadWeeklyStats,
  studentId,
  setSelectedSkill,
  setSelectedModalSkill,
  siteId,
}) => {
  const isrRedux = useAppSelector(selectedIndividualStudentReportState);
  const dispatch = useAppDispatch();
  const [inSchoolYear, setInSchoolYear] = useState(true);

  useEffect(() => {
    if (isrRedux?.dates) {
      loadWeeklyStats(
        {
          userId: `UserNode:${splitId(studentId)}`,
          from: isrRedux.dates[0],
          to: new Date(isrRedux.dates[1].toLocaleDateString('en-US')),
          siteIds: siteId ? [siteId] : null,
          classExitSlipsFilter: {
            userIds: [`UserNode:${splitId(studentId)}`],
            dueOnFrom: format(isrRedux.dates[0], 'yyyy-MM-dd'),
            dueOnTo: format(min([isrRedux.dates[1], new Date()]), 'yyyy-MM-dd'),
          },
        },
        { fetchPolicy: 'network-only' },
      );
    }
  }, [isrRedux?.dates, studentId]);

  const weekHandler = (index: 1 | -1) => {
    if (isrRedux?.dates) {
      if (index === 1) {
        return dispatch(
          setIndividualStudentReport({
            ...isrRedux,
            dates: [
              startOfWeek(addDays(isrRedux?.dates[0], 7), { weekStartsOn: 1 }),
              endOfWeek(addDays(isrRedux?.dates[0], 7), { weekStartsOn: 1 }),
            ],
          }),
        );
      }
      if (index === -1) {
        return dispatch(
          setIndividualStudentReport({
            ...isrRedux,
            dates: [
              startOfWeek(sub(isrRedux?.dates[0], { days: 7 }), { weekStartsOn: 1 }),
              endOfWeek(sub(isrRedux?.dates[0], { days: 7 }), { weekStartsOn: 1 }),
            ],
          }),
        );
      }
    }
  };

  return (
    <div>
      <div className="tw-flex tw-items-center tw-w-full">
        <h3>Skill learning activity</h3>
        <div className="tw-flex tw-items-center tw-w-[80%]">
          {isrRedux?.dates?.length === 2 && (
            <div className="flex align-items-center sm:col-12 md:flex-row flex-column">
              <div className="flex align-items-center xl:col-4 md:col-6" id="SkillChallengesWeekPicker">
                <Button
                  id="isrBackButtonWeeklyPicker"
                  style={{ borderRadius: '1rem 0 0 1rem' }}
                  icon={<ChevronLeft size={15} />}
                  disabled={inSchoolYear || (isrRedux?.dates?.length && isBefore(isrRedux?.dates[0], '2026-02-15'))}
                  outlined
                  onClick={() => {
                    weekHandler(-1);
                  }}
                  aria-label={'Previous week'}
                />

                <Tooltip
                  showOnDisabled={isBefore(isrRedux?.dates[0], '2026-02-15')}
                  disabled={!isBefore(isrRedux?.dates[0], '2026-02-15')}
                  position="top"
                  target="#isrBackButtonWeeklyPicker"
                >
                  <div>
                    <p className="tw-text-center tw-text-neutral-300">
                      <b className="tw-text-xs">
                        Time tracking data is only available starting from when this feature was introduced.
                      </b>
                    </p>
                  </div>
                </Tooltip>

                <label style={{ border: '1px solid', borderColor: '#1259fb', padding: '0.5rem' }}>
                  {format(isrRedux?.dates[0], 'P')} - {format(isrRedux?.dates[1], 'P')}
                </label>
                <Button
                  style={{ borderRadius: '0 1rem 1rem 0' }}
                  icon={<ChevronRight size={15} />}
                  disabled={isrRedux.dates[1] > new Date()}
                  outlined
                  onClick={() => {
                    weekHandler(1);
                  }}
                  aria-label={'Next week'}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      <hr className="tw-mt-5 tw-text-neutral-300" style={{ opacity: 0.6 }} />
      {weeklyStatsRef && (
        <>
          <Suspense fallback={<ThreeDotLoader dark />}>
            <IndividualReportWeeklyStats weeklyStatsRef={weeklyStatsRef} setInSchoolYear={setInSchoolYear} />
          </Suspense>

          <Suspense fallback={<ThreeDotLoader dark />}>
            <IndividualReportDailyActivity
              weeklyStatsRef={weeklyStatsRef}
              setSelectedSkill={setSelectedSkill}
              setSelectedModalSkill={setSelectedModalSkill}
            />
          </Suspense>
        </>
      )}
    </div>
  );
};
